import { useEffect, useMemo, useRef, useState } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import MyVendorRotatingLogo from "./MyVendorRotatingLogo";
import { MyVendorServiceCategoryRow } from "./MyVendorServiceCategoryChip";
import DeleteVendorListModal from "./DeleteVendorListModal";
import {
  VENDOR_LIST_CARD_MAX_LOGOS,
  VENDOR_LIST_CARD_MAX_NOTES_LENGTH,
  isSystemListId,
} from "../../utils/myVendorListsConstants";
import { extractVendorListMembers } from "../../utils/myVendorListsUtils";

function VendorListLogo({ member }) {
  const [showInitials, setShowInitials] = useState(!member.logoUrl);
  const initials = (member.name || "?").substring(0, 2).toUpperCase();

  return (
    <div className="flex h-full w-full items-center justify-center overflow-hidden">
      {!showInitials && member.logoUrl ? (
        <MyVendorRotatingLogo
          vendorName={member.name}
          logoUrl={member.logoUrl}
          className="h-full w-full object-contain"
          style={{ padding: "4.25px" }}
          onExhausted={() => setShowInitials(true)}
        />
      ) : (
        <span className="text-center text-sm font-semibold text-gray-400 sm:text-base lg:text-xl">
          {initials}
        </span>
      )}
    </div>
  );
}

function VendorListActionMenu({
  list,
  onRename,
  onDuplicate,
  onDelete,
  onExportCsv,
  onExportExcel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const menuRef = useRef(null);
  const isSystem = isSystemListId(list.id);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const timer = setTimeout(() => document.addEventListener("mousedown", handleClickOutside), 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleRename = (event) => {
    event.stopPropagation();
    setIsOpen(false);
    onRename?.(list);
  };

  const handleDuplicate = async (event) => {
    event.stopPropagation();
    setIsOpen(false);
    await onDuplicate?.(list);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    setIsOpen(false);
    setShowDeleteModal(true);
  };

  const handleExportCsv = (event) => {
    event.stopPropagation();
    setIsOpen(false);
    onExportCsv?.(list);
  };

  const handleExportExcel = async (event) => {
    event.stopPropagation();
    setIsOpen(false);
    setIsExportingExcel(true);
    try {
      await onExportExcel?.(list);
    } catch (error) {
      console.error("Excel export failed:", error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        aria-label="List actions"
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      {isOpen ? (
        <div
          className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md bg-white py-2 shadow-lg ring-1 ring-black ring-opacity-5"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={handleExportCsv}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2 h-4 w-4 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Export CSV
          </button>
          <button
            type="button"
            className={`flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
              isExportingExcel ? "cursor-wait opacity-50" : ""
            }`}
            onClick={handleExportExcel}
            disabled={isExportingExcel}
          >
            {isExportingExcel ? (
              <svg
                className="mr-2 h-4 w-4 animate-spin text-green-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-4 w-4 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            )}
            {isExportingExcel ? "Exporting..." : "Export Excel"}
          </button>
          {!isSystem ? (
            <button
              type="button"
              className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={handleRename}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Rename List
            </button>
          ) : null}
          <button
            type="button"
            className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={handleDuplicate}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Duplicate List
          </button>
          {!isSystem ? (
            <button
              type="button"
              className="flex w-full items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              onClick={handleDelete}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Delete List
            </button>
          ) : null}
        </div>
      ) : null}

      <DeleteVendorListModal
        isOpen={showDeleteModal}
        listName={list.name}
        onConfirm={() => {
          onDelete?.(list);
          setShowDeleteModal(false);
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

function formatListDate(value) {
  if (!value) return null;
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
}

export default function VendorListCard({
  list,
  isNewlyCreated = false,
  onEditModeEntered,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
  onExportCsv,
  onExportExcel,
}) {
  const isSystemList = Boolean(list.isSystemList || isSystemListId(list.id));
  const [isRenaming, setIsRenaming] = useState(isNewlyCreated && !isSystemList);
  const [editedName, setEditedName] = useState(list.name);

  const members = useMemo(() => extractVendorListMembers(list), [list]);
  const displayVendors = members.slice(0, VENDOR_LIST_CARD_MAX_LOGOS);
  const remainingCount = Math.max(0, members.length - VENDOR_LIST_CARD_MAX_LOGOS);

  const sortedServices = useMemo(() => {
    const frequency = {};
    members.forEach((member) => {
      (member.serviceCategories || []).forEach((service) => {
        frequency[service] = (frequency[service] || 0) + 1;
      });
    });
    return Object.entries(frequency)
      .sort((a, b) => b[1] - a[1])
      .map(([service]) => service);
  }, [members]);

  const truncatedNotes =
    list.notes && list.notes.length > VENDOR_LIST_CARD_MAX_NOTES_LENGTH
      ? `${list.notes.substring(0, VENDOR_LIST_CARD_MAX_NOTES_LENGTH)}…`
      : list.notes;

  const createdDateLabel = formatListDate(list.createdAt);

  useEffect(() => {
    setEditedName(list.name);
  }, [list.name]);

  useEffect(() => {
    if (isNewlyCreated && isRenaming && onEditModeEntered) {
      onEditModeEntered();
    }
  }, [isNewlyCreated, isRenaming, onEditModeEntered]);

  const handleCardClick = () => {
    if (!isRenaming) {
      onOpen?.(list);
    }
  };

  const handleSaveRename = async () => {
    const trimmed = editedName.trim();
    if (trimmed && trimmed !== list.name) {
      await onRename?.(list, trimmed);
    }
    setIsRenaming(false);
  };

  const handleCancelRename = () => {
    setEditedName(list.name);
    setIsRenaming(false);
  };

  return (
    <div
      className="relative flex h-full cursor-pointer flex-col rounded-lg border border-gray-200 bg-white pt-0 shadow-sm transition-shadow duration-200 hover:shadow-md"
      onClick={handleCardClick}
      data-created-by={list.createdByName || undefined}
      data-created-at={list.createdAt || undefined}
    >
      <div className="absolute right-4 top-4 z-10">
        <VendorListActionMenu
          list={list}
          onRename={() => setIsRenaming(true)}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onExportCsv={onExportCsv}
          onExportExcel={onExportExcel}
        />
      </div>

      <div className="relative flex min-h-[8rem] items-center justify-center overflow-hidden rounded-t-lg border-b border-gray-100 bg-gray-50 px-6 pb-8 pt-8 sm:min-h-[9rem] lg:min-h-[10rem]">
        <div className="flex items-center justify-center">
          {displayVendors.map((member, index) => (
            <div
              key={member.providerId || member.name}
              className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-gray-50 shadow-sm sm:h-20 sm:w-20 lg:h-24 lg:w-24"
              style={{
                marginLeft: index > 0 ? "-1rem" : "0",
                zIndex: displayVendors.length - index,
                boxShadow:
                  "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <VendorListLogo member={member} />
            </div>
          ))}
          {remainingCount > 0 ? (
            <div
              className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border-2 border-white bg-indigo-100 shadow-sm sm:h-16 sm:w-16 lg:h-20 lg:w-20"
              style={{
                marginLeft: "-0.75rem",
                zIndex: 0,
                boxShadow:
                  "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <span className="text-sm font-semibold text-indigo-700 sm:text-base lg:text-xl">
                +{remainingCount}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col divide-y divide-gray-100 px-6 py-2">
        <div className="py-3">
          {isRenaming ? (
            <div className="flex min-w-0 items-center gap-2">
              <input
                type="text"
                value={editedName}
                onChange={(event) => setEditedName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSaveRename();
                  if (event.key === "Escape") handleCancelRename();
                }}
                className="min-w-0 flex-1 rounded border border-indigo-300 px-2 py-1 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
                onClick={(event) => event.stopPropagation()}
              />
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSaveRename();
                  }}
                  className="inline-flex shrink-0 items-center justify-center rounded-md bg-green-100 p-1.5 text-green-600 transition-colors hover:bg-green-200"
                  title="Save"
                  aria-label="Save list name"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCancelRename();
                  }}
                  className="inline-flex shrink-0 items-center justify-center rounded-md bg-red-100 p-1.5 text-red-600 transition-colors hover:bg-red-200"
                  title="Cancel"
                  aria-label="Cancel rename"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">{list.name}</h3>
              {isSystemList ? (
                <span
                  className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 ring-1 ring-inset ring-gray-200"
                  title="Default list — cannot be renamed or deleted"
                >
                  Default
                </span>
              ) : null}
            </div>
          )}
          <p className="mt-1 text-sm text-gray-500">
            {members.length} {members.length === 1 ? "vendor" : "vendors"}
          </p>
          {!isSystemList && (list.createdByName || createdDateLabel) ? (
            <p className="sr-only">
              Created by {list.createdByName || "Unknown"}
              {createdDateLabel ? ` on ${createdDateLabel}` : ""}
            </p>
          ) : null}
        </div>

        {sortedServices.length > 0 ? (
          <div className="py-3">
            <span className="mb-2 block text-sm text-gray-500">Services Summary</span>
            <MyVendorServiceCategoryRow services={sortedServices} maxVisible={5} />
          </div>
        ) : null}

        {list.notes ? (
          <div className="py-3">
            <span className="mb-1 block text-sm text-gray-500">Notes</span>
            <p className="line-clamp-3 text-sm text-gray-700">{truncatedNotes}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
